import { Link } from 'react-router-dom';
import { useFeaturedVehicles } from '@/features/search/queries';
import { VehicleCard, VehicleCardSkeleton } from '@/features/vehicles/VehicleCard';
import { useSearchListings } from '@/features/listings/queries';
import { ListingCard, ListingCardSkeleton } from '@/features/listings/ListingCard';
import { useCities, useVehicleCategories } from '@/features/platform/queries';
import { ROUTES } from '@/config/navigation';
import { BRAND_HIGHLIGHTS, BRAND_STEPS } from '@/config/brand';
import { HeroHeadline } from '@/features/home/HeroHeadline';
import { HeroVisuel } from '@/features/home/HeroVisuel';
import { ButtonLink } from '@/components/ui/Button';
import { VEHICLE_CATEGORIES } from '@/config/platform';
import { isSupabaseConfigured } from '@/lib/supabase/client';

const trust = [
  {
    title: 'Agences vérifiées',
    text: 'Documents de société et papiers des véhicules contrôlés avant la mise en ligne.',
  },
  {
    title: 'Prix calculé par nos serveurs',
    text: 'Le montant affiché est celui qui sera facturé. Aucun ajustement à la prise en charge.',
  },
  {
    title: 'Caution tracée',
    text: 'Encaissement, blocage, restitution ou retenue motivée : chaque étape est enregistrée.',
  },
  {
    title: 'Contrat numérique',
    text: 'Un contrat clair, figé au moment de la signature, consultable à tout moment.',
  },
];

/** Titre de section : même rythme partout sur la page. */
function SectionTitle({
  title,
  subtitle,
  action,
  centered = false,
}: {
  title: string;
  subtitle?: string;
  action?: { label: string; to: string };
  centered?: boolean;
}) {
  return (
    <div
      className={
        centered
          ? 'mb-6 text-center'
          : 'mb-5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1'
      }
    >
      <div>
        <h2 className="text-[22px] font-extrabold text-ink-950">{title}</h2>
        {subtitle && <p className="mt-1.5 text-sm text-ink-500">{subtitle}</p>}
      </div>
      {action && (
        <Link
          to={action.to}
          className="whitespace-nowrap text-sm font-bold text-brand-600 hover:text-brand-700"
        >
          {action.label}
        </Link>
      )}
    </div>
  );
}

export default function HomePage() {
  const featured = useFeaturedVehicles(undefined, 6);
  // Les dernières annonces publiées. `search_listings` ne renvoie que celles
  // qui ont passé la validation : aucun filtre de statut à poser ici.
  const listings = useSearchListings({ tri: 'recent', limit: 4 });
  const listingItems = listings.data?.items ?? [];
  const cities = useCities();
  const categoriesQuery = useVehicleCategories();
  const categories = categoriesQuery.data ?? VEHICLE_CATEGORIES.map((c) => ({ ...c }));

  return (
    <div className="cf-container pb-16 pt-[clamp(28px,5vw,56px)]">
      {/* ------------------------------------------------------------------ */}
      {/* Accroche et visuel                                                  */}
      {/* ------------------------------------------------------------------ */}
      {/* Le formulaire de recherche a été retiré d'ici : l'accroche porte
          désormais trois messages qui défilent (louer, acheter, vendre) et
          chacun a son propre bouton. Un panneau de recherche figé sur la
          location, juste en dessous, disait le contraire de l'accroche —
          « tout est possible » au-dessus, « en fait, on loue » au-dessous.
          La recherche complète reste à un clic, dans « Louer ».

          Deux colonnes à partir de 1024 px : les messages à gauche, la voiture
          à droite. En dessous, la grille retombe sur une seule colonne et la
          voiture passe sous les messages — sur un téléphone, les mettre côte à
          côte réduirait les deux à rien. */}
      <section className="grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <HeroHeadline />
        <HeroVisuel />
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Bandeau de garanties                                                */}
      {/* ------------------------------------------------------------------ */}
      <section className="my-[clamp(36px,5vw,56px)] flex flex-wrap justify-center gap-x-[clamp(20px,4vw,44px)] gap-y-4 border-y border-ink-200 py-7">
        {BRAND_HIGHLIGHTS.map((item) => (
          <p
            key={item.label}
            className="flex items-center gap-2.5 text-[13px] font-semibold text-ink-700"
          >
            <span className="h-2 w-2 shrink-0 rounded-full bg-brand-500" />
            {item.label}
          </p>
        ))}
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Catégories                                                          */}
      {/* ------------------------------------------------------------------ */}
      <section className="mb-[clamp(36px,5vw,56px)]">
        <SectionTitle
          title="Catégories"
          subtitle="De la citadine au 4x4, avec ou sans chauffeur."
          action={{ label: 'Voir tout', to: ROUTES.search }}
        />
        <ul className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(120px,1fr))]">
          {categories.map((category) => (
            <li key={category.slug}>
              <Link
                to={`${ROUTES.search}?categories=${category.slug}`}
                className="block rounded-xl border border-ink-200 px-2.5 py-4 text-center text-[13px] font-semibold text-ink-950 transition-colors hover:border-brand-400 hover:text-brand-600"
              >
                {category.label}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Meilleures offres                                                   */}
      {/* ------------------------------------------------------------------ */}
      <section className="mb-[clamp(40px,6vw,64px)]">
        <SectionTitle
          title="Nos meilleures offres"
          subtitle="Les véhicules les plus loués du moment."
          action={{ label: 'Voir tout', to: ROUTES.search }}
        />

        <div className="grid gap-6 [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]">
          {featured.isLoading &&
            Array.from({ length: 3 }).map((_, index) => <VehicleCardSkeleton key={index} />)}
          {(featured.data ?? []).map((vehicle) => (
            <VehicleCard key={vehicle.id} vehicle={vehicle} />
          ))}
        </div>

        {!isSupabaseConfigured && (
          <p className="mt-4 rounded-2xl border border-dashed border-ink-300 p-4 text-sm text-ink-500">
            Les véhicules sont lus depuis la base de données. Connectez Supabase pour les
            afficher — rien n’est inventé ici.
          </p>
        )}
        {featured.data && featured.data.length === 0 && (
          <p className="mt-4 rounded-2xl border border-dashed border-ink-300 p-4 text-sm text-ink-500">
            Aucun véhicule publié pour l’instant. Les premières agences arrivent.
          </p>
        )}
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Voitures à vendre                                                   */}
      {/* ------------------------------------------------------------------ */}
      {/* La location reste la première offre : elle passe avant. Mais un
          visiteur qui cherche à acheter n'a aucune raison de deviner que la
          plateforme le permet aussi — il faut le lui montrer, pas seulement
          l'écrire dans le menu. La section disparaît tant qu'aucune annonce
          n'est publiée : un catalogue vide sur l'accueil dessert le service. */}
      {(listings.isLoading || listingItems.length > 0) && (
        <section className="mb-[clamp(40px,6vw,64px)]">
          <SectionTitle
            title="Voitures à vendre"
            subtitle="Annonces vérifiées avant publication. Vous traitez directement avec le vendeur."
            action={{ label: 'Voir tout', to: ROUTES.listings }}
          />

          <div className="grid gap-6 [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]">
            {listings.isLoading &&
              Array.from({ length: 4 }).map((_, index) => <ListingCardSkeleton key={index} />)}
            {listingItems.map((item) => (
              <ListingCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Entretenir sa voiture                                               */}
      {/* ------------------------------------------------------------------ */}
      {/* Louer, acheter, vendre — et entretenir. C'est le seul des quatre qui
          concerne quelqu'un qui a DÉJÀ sa voiture, c'est-à-dire la plupart des
          visiteurs. Deux portes, une par métier : on ne cherche pas un lavage
          comme on cherche un mécanicien. */}
      <section className="mb-[clamp(40px,6vw,64px)]">
        <SectionTitle
          title="Entretenir sa voiture"
          subtitle="Trouvez un garage ou un lavage près de chez vous, voyez les tarifs avant de vous déplacer, demandez un rendez-vous."
        />
        <div className="grid gap-5 sm:grid-cols-2">
          <Link
            to={ROUTES.garages}
            className="group rounded-2xl border border-ink-200 p-6 transition-colors hover:border-ink-950"
          >
            <h3 className="text-lg font-bold text-ink-950">Garages mécaniques</h3>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-600">
              Vidange, freins, diagnostic, climatisation. Tarifs affichés en fourchette, rendez-vous
              confirmé par le garage.
            </p>
            <span className="mt-4 inline-block text-sm font-semibold text-brand-600 group-hover:text-brand-700">
              Voir les garages →
            </span>
          </Link>

          <Link
            to={ROUTES.carwashes}
            className="group rounded-2xl border border-ink-200 p-6 transition-colors hover:border-ink-950"
          >
            <h3 className="text-lg font-bold text-ink-950">Lavage auto</h3>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-600">
              Extérieur, intérieur, moteur. Comparez les formules et réservez votre passage.
            </p>
            <span className="mt-4 inline-block text-sm font-semibold text-brand-600 group-hover:text-brand-700">
              Voir les lavages →
            </span>
          </Link>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Comment ça marche                                                   */}
      {/* ------------------------------------------------------------------ */}
      <section className="mb-[clamp(40px,6vw,64px)]">
        <SectionTitle title="Comment ça marche" centered />
        <ol className="grid gap-7 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
          {BRAND_STEPS.map((step) => (
            <li key={step.title} className="text-center">
              <span className="mx-auto mb-3.5 flex h-[52px] w-[52px] items-center justify-center rounded-full bg-brand-50 text-xl font-extrabold text-brand-600">
                {step.number}
              </span>
              <h3 className="text-[15px] font-bold text-ink-950">{step.title}</h3>
              <p className="mx-auto mt-1.5 max-w-[26ch] text-[13px] leading-relaxed text-ink-500">
                {step.text}
              </p>
            </li>
          ))}
        </ol>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Garanties détaillées                                                */}
      {/* ------------------------------------------------------------------ */}
      <section className="mb-[clamp(40px,6vw,64px)] grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        {trust.map((item) => (
          <div key={item.title} className="rounded-2xl border border-ink-200 p-5">
            <h3 className="text-sm font-bold text-ink-950">{item.title}</h3>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-500">{item.text}</p>
          </div>
        ))}
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Villes                                                              */}
      {/* ------------------------------------------------------------------ */}
      <section className="mb-[clamp(36px,5vw,56px)]">
        <SectionTitle title="Villes desservies" />
        <ul className="flex flex-wrap gap-2.5">
          {(cities.data ?? []).map((city) => (
            <li key={city.slug}>
              <Link
                to={ROUTES.cityLanding(city.slug)}
                className="inline-flex items-center gap-1.5 rounded-full border border-ink-200 px-4 py-2 text-[13px] font-semibold text-ink-950 hover:border-brand-400 hover:text-brand-600"
              >
                {city.name}
                {!city.is_active && <span className="font-medium text-ink-400">(bientôt)</span>}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Appel aux agences                                                   */}
      {/* ------------------------------------------------------------------ */}
      {/* Deux appels, pas un : jusqu'ici l'accueil ne proposait rien à celui
          qui veut vendre sa voiture, alors que c'est un compte à part entière. */}
      <section className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]">
        <div className="flex flex-col rounded-3xl bg-ink-950 p-[clamp(24px,4vw,36px)]">
          <h2 className="text-lg font-extrabold text-white">Vous êtes une agence de location ?</h2>
          <p className="mt-1.5 text-sm text-ink-300">
            Publiez votre flotte, gérez vos disponibilités et recevez vos réservations en ligne.
            Sans commission.
          </p>
          <ButtonLink to={ROUTES.becomePartner} className="mt-5 self-start whitespace-nowrap">
            Devenir partenaire
          </ButtonLink>
        </div>

        <div className="flex flex-col rounded-3xl border border-ink-200 bg-ink-50 p-[clamp(24px,4vw,36px)]">
          <h2 className="text-lg font-extrabold text-ink-950">Vous vendez votre voiture ?</h2>
          <p className="mt-1.5 text-sm text-ink-600">
            Publiez gratuitement votre annonce. Nous la vérifions, les acheteurs vous écrivent
            directement.
          </p>
          <ButtonLink
            to={ROUTES.becomeSeller}
            variant="dark"
            className="mt-5 self-start whitespace-nowrap"
          >
            Vendre ma voiture
          </ButtonLink>
        </div>
      </section>
    </div>
  );
}
